import orderBy from 'lodash/orderBy';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import FeatureTogglesContext from '../../../contexts/FeatureTogglesContext';
import IdentityContext from '../../../contexts/IdentityContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { getSharedSlideSets, ListSharedSlideSetsQuery, SharedSlideSet } from '../_api/SlideSetApi';
import { SharedUserMaterial, getSharedUserMaterialWithScores } from '../_api/UserMaterialApi';
import ModuleContext from './ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type FetchMaterial = (opts?: Omit<ListSharedSlideSetsQuery, 'nodeId'>) => Promise<void>;

type SharedMaterialContextValue = {
  sharedMaterial: (SharedUserMaterial | SharedSlideSet)[];
  fetchSharedMaterial: FetchMaterial;
};

const SharedMaterialContext = createContext<SharedMaterialContextValue>({
  sharedMaterial: [],
  fetchSharedMaterial: () => Promise.resolve(),
});

export default SharedMaterialContext;

export function SharedMaterialContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const featureToggles = useContext(FeatureTogglesContext);

  const [sharedMaterial, setSharedMaterial] = useState<(SharedUserMaterial | SharedSlideSet)[]>([]);

  const fetchSharedMaterial = useCallback<FetchMaterial>(
    async options => {
      if (!selectedModule || !selectedNode) return;
      try {
        const [sharedUserMaterialResponse, sharedSlideSetsResponse] = await Promise.all([
          getSharedUserMaterialWithScores(selectedModule.id, selectedNode.id),
          featureToggles.SHARE_SLIDE_SET
            ? getSharedSlideSets(selectedModule.id, { nodeId: selectedNode.id, ...(options || {}) })
            : Promise.resolve({ data: [] }),
        ]);

        setSharedMaterial([...sharedUserMaterialResponse.data, ...sharedSlideSetsResponse.data]);
      } catch (e) {
        handleError(e);
      }
    },
    [featureToggles.SHARE_SLIDE_SET, handleError, selectedModule, selectedNode],
  );

  useEffect(() => {
    if (identity.isAuthenticated && !identity.isTeacherAlike) fetchSharedMaterial();
  }, [fetchSharedMaterial, identity.isAuthenticated, identity.isTeacherAlike]);

  const sortedSharedMaterial = useMemo(() => orderBy(sharedMaterial, ['lastUpdatedAt'], ['desc']), [sharedMaterial]);

  return (
    <SharedMaterialContext.Provider
      value={{
        sharedMaterial: sortedSharedMaterial,
        fetchSharedMaterial,
      }}
    >
      {children}
    </SharedMaterialContext.Provider>
  );
}
